import React, { useContext, useMemo, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { Column } from "iuvity-ui-components/dist/column";
import { TableHeader } from "../datatable/TableHeader";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { PickListModal } from "../shared/PickListModal";
import { useTransactionFields } from "../../hooks/useTransactionFields";
import { PRODUCT_STATUS_OPTIONS } from "../../constants";
import { NewDialogProps } from "../../components/dialog/NewCustomDialog";
import { AppContext } from "../../context/index";

interface TrxDataTableActionsProps {
  serviceApi: string;
  auxModalController: React.Dispatch<React.SetStateAction<NewDialogProps>>;
  limit?: number;
}

export const TrxDataTableActions: React.FC<TrxDataTableActionsProps> = ({
  serviceApi,
  auxModalController,
  limit = 0,
}) => {
  const { t } = useContext(AppContext);

  const {
    mappedFields,
    transactionFields,
    transactionFieldAction,
    filteredData,
    setFilteredData,
    selectedFieldsIds,
    reloadFn,
  } = useTransactionFields(serviceApi);

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        header: t("Products.nameLabel"),
      },
      {
        field: "labelkey",
        header: t("Products.aliasLabel"),
      },
      {
        field: "order",
        header: t("Products.orderLabel"),
      },
      {
        field: "status",
        header: t("Products.statusLabel"),
      },
    ];
  }, [t]);

  const [visible, setVisible] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [editingRows, setEditingRows] = useState({});
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowEditChange = (e: any) => {
    setEditingRows(e.data);
  };

  const onRowEditComplete = async (e: any) => {
    let { newData } = e;
    setFilteredData((prev: any) =>
      prev?.map((mpf: any) => ({
        ...mpf,
        status: mpf?.id === newData?.id ? newData?.status : mpf?.status,
      }))
    );

    const updateStatus = await transactionFieldAction("FIELD", "UPDATE", [
      {
        id: newData.transactionfieldid,
        status: newData.status,
      },
    ]);
    if (updateStatus === "SUCCESS") {
      reloadFn();
    }
  };

  const statusEditor = (options: any) => {
    return (
      // @ts-ignore
      <Dropdown
        label=""
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
        options={PRODUCT_STATUS_OPTIONS}
        filter={false}
        style={{
          border: "1px solid darkgray",
          borderRadius: "0.5rem",
          background: "white",
          padding: "0.5rem",
          maxWidth: "10rem",
        }}
      />
    );
  };

  const handleModalProps = (editorEvent: any) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Products.saveModalTitle"),
      content: t("Products.saveModalContent"),
      onConfirm: () => onRowEditComplete(editorEvent),
    }));
  };

  return (
    <>
      <SubheaderActions
        className="mx-3 pt-3 px-0"
        actions={[
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Products.addOrRemoveLabel"),
            onClick: () => setVisible(true),
          },
        ]}
      />

      {filteredData ? (
        <DataTable
          className="m-3"
          header={
            <TableHeader
              title={t("Products.selectedFieldsLabel")}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={filters}
              setFilters={setFilters}
              reload={reloadFn}
            />
          }
          sortField="order"
          sortOrder={1}
          editMode="row"
          dataKey="mappedfieldid"
          editingRows={editingRows}
          onRowEditChange={onRowEditChange}
          onRowEditComplete={handleModalProps}
          emptyMessage={t("TableActions.emptyMessage")}
          rows={10}
          size="normal"
          filters={filters}
          value={filteredData}
          scrollable
          resizableColumns
        >
          {selectedColumns.map((col, idx) => {
            return (
              <Column
                key={`${col.field}-${idx}`}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                editor={
                  col.field === "status" && ((options) => statusEditor(options))
                }
                align="left"
              />
            );
          })}
          <Column
            key="actions"
            header={t("Products.actionsLabel")}
            align="center"
            rowEditor
            headerStyle={{ justifyContent: "center" }}
            style={{
              boxShadow: "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px",
              whiteSpace: "normal",
            }}
            frozen
            alignFrozen="right" 
          />
        </DataTable>
      ) : (
        <BoxFull className="mx-2 p-0" style={{ background: "transparent" }}>
          <FormSkeleton length={3} />
        </BoxFull>
      )}

      {mappedFields && transactionFields && (
        <PickListModal
          sourceData={mappedFields
            .filter((field: any) => !selectedFieldsIds.includes(field.id))
            .map((field: any) => ({
              id: field.mappedfieldid,
              label: field.name,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label))}
          targetData={transactionFields
            .map((field: any) => ({
              id: field.mappedfieldid,
              label: field.name,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label))}
          visible={visible}
          setVisible={setVisible}
          actionFn={transactionFieldAction}
          reloadFn={reloadFn}
          auxModalController={auxModalController}
          limit={limit}
        />
      )}
    </>
  );
};
